import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { appLogger } from '@/lib/logger';
import {
  isPushSupported,
  getPushPermissionState,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  savePushSubscription,
  removePushSubscription,
  getStoredVapidKey,
  setStoredVapidKey,
  type PushPermissionState,
} from '@/lib/pushNotifications';

const VAPID_PUBLIC_KEY_ENV = import.meta.env.VITE_VAPID_PUBLIC_KEY;
const PUSH_DISMISSED_KEY = 'worklynx_push_dismissed';

export function usePushNotifications() {
  const userId = useAuthStore((s) => s.user?.id);
  const orgId = useOrgStore((s) => s.organization?.id);
  const [permissionState, setPermissionState] = useState<PushPermissionState>(getPushPermissionState);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(PUSH_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const supported = isPushSupported();
  const vapidKey = VAPID_PUBLIC_KEY_ENV || getStoredVapidKey();

  useEffect(() => {
    if (!supported) return;
    setPermissionState(getPushPermissionState());
  }, [supported]);

  // Auto-register service worker on mount
  useEffect(() => {
    if (!supported) return;
    registerServiceWorker();
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!userId || !orgId || !supported) return false;

    setIsSubscribing(true);
    try {
      const permission = await requestNotificationPermission();
      setPermissionState(permission);

      if (permission !== 'granted') return false;

      const effectiveVapidKey = VAPID_PUBLIC_KEY_ENV || getStoredVapidKey();
      if (!effectiveVapidKey) {
        appLogger.warn('No VAPID public key configured. Set VITE_VAPID_PUBLIC_KEY in .env.');
        return false;
      }

      const registration = await registerServiceWorker();
      if (!registration) return false;

      const subscription = await subscribeToPush(registration, effectiveVapidKey);
      if (!subscription) return false;

      await savePushSubscription(userId, orgId, subscription);
      if (effectiveVapidKey) setStoredVapidKey(effectiveVapidKey);

      return true;
    } catch (error) {
      appLogger.error('Push subscription failed:', error);
      return false;
    } finally {
      setIsSubscribing(false);
    }
  }, [userId, orgId, supported]);

  const unsubscribe = useCallback(async () => {
    if (!userId) return;
    await removePushSubscription(userId);
    setPermissionState(getPushPermissionState());
  }, [userId]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      localStorage.setItem(PUSH_DISMISSED_KEY, 'true');
    } catch {
      // Silently ignore storage errors
    }
  }, []);

  const shouldShowPrompt =
    supported &&
    permissionState === 'default' &&
    !isDismissed &&
    !!userId;

  return {
    supported,
    permissionState,
    isSubscribing,
    shouldShowPrompt,
    hasVapidKey: !!vapidKey,
    subscribe,
    unsubscribe,
    dismiss,
  };
}
